import { useForm, Controller } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select, type SelectOption } from "@/components/ui/Select";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import type { Goal } from "./CasesList";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GoalFormValues {
  goalText: string;
  targetDate: string;
  status: string;
  outcome: string;
}

interface GoalFormProps {
  personId: string;
  caseId: number;
  goal?: Goal;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const goalStatusOptions: SelectOption[] = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "achieved", label: "Achieved" },
  { value: "not_achieved", label: "Not Achieved" },
  { value: "deferred", label: "Deferred" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GoalForm({ personId, caseId, goal, onClose }: GoalFormProps) {
  const queryClient = useQueryClient();
  const isEditing = !!goal;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<GoalFormValues>({
    defaultValues: {
      goalText: goal?.goal_text ?? "",
      targetDate: goal?.target_date ?? "",
      status: goal?.status ?? "pending",
      outcome: goal?.outcome ?? "",
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: GoalFormValues) =>
      api.post(`/clients/${personId}/cases/${caseId}/goals`, {
        goalText: data.goalText,
        targetDate: data.targetDate || undefined,
        status: data.status,
        outcome: data.outcome || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals", caseId] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: GoalFormValues) =>
      api.put(`/clients/${personId}/cases/${caseId}/goals/${goal!.id}`, {
        goalText: data.goalText,
        targetDate: data.targetDate || undefined,
        status: data.status,
        outcome: data.outcome || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals", caseId] });
      onClose();
    },
  });

  const mutation = isEditing ? updateMutation : createMutation;

  function onSubmit(data: GoalFormValues) {
    mutation.mutate(data);
  }

  return (
    <Card className="border-primary/30">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">
          {isEditing ? "Edit Goal" : "New Goal"}
        </CardTitle>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Goal Text <span className="text-destructive">*</span>
            </label>
            <textarea
              {...register("goalText", {
                required: "Goal text is required",
              })}
              rows={3}
              placeholder="Describe the goal..."
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {errors.goalText && (
              <p className="text-xs text-destructive">
                {errors.goalText.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              label="Target Date"
              type="date"
              {...register("targetDate")}
            />

            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  label="Status"
                  options={goalStatusOptions}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Outcome
            </label>
            <textarea
              {...register("outcome")}
              rows={2}
              placeholder="Outcome notes..."
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {/* Error */}
          {mutation.isError && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {(mutation.error as Error)?.message ?? "An error occurred"}
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" size="sm" loading={mutation.isPending}>
              {isEditing ? "Save" : "Create Goal"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
